import { FC } from "react";
import { Page } from "../../../Components/Page";
import {
  ActionIcon,
  Button,
  Group,
  Indicator,
  Loader,
  Skeleton,
  Table,
  Text,
} from "@mantine/core";
import { Link } from "react-router-dom";
import { components } from "../../../openapi/generated-types";
import { DateTime } from "luxon";
import { useRequiredParams } from "../../../hooks/useRequiredParams";
import { ResolvedTeamReference } from "../../../Components/ResolvedTeamReference";
import {
  IconDownload,
  IconEdit,
  IconListNumbers,
  IconPdf,
} from "@tabler/icons-react";
import { TeamName } from "../../../Components/TeamName";
import { Time } from "../../../Components/Time";
import { useCompetition } from "../../../hooks/useCompetition.ts";
import { useGameList } from "../../../hooks/useGameList.ts";

interface TableRowProps {
  game: components["schemas"]["Game"];
}

const TableRow: FC<TableRowProps> = (props) => {
  const { game } = props;

  const competition = useCompetition(game.competitionId);

  if (!competition.isSuccess) {
    return (
      <Table.Tr>
        <Table.Td>
          <Skeleton height="16px" />
        </Table.Td>
        <Table.Td>
          <Skeleton height="16px" />
        </Table.Td>
        <Table.Td>
          <Skeleton height="16px" mb="4px" />
          <Skeleton height="16px" />
        </Table.Td>
        <Table.Td>
          <Skeleton height="16px" mb="4px" />
          <Skeleton height="16px" />
        </Table.Td>
        <Table.Td>
          <Skeleton height="16px" />
        </Table.Td>
        <Table.Td>
          <Skeleton height="16px" />
        </Table.Td>
        <Table.Td>
          <Skeleton height="16px" />
        </Table.Td>
        <Table.Td>
          <Skeleton height="16px" />
        </Table.Td>
        <Table.Td>
          <Skeleton height="16px" />
        </Table.Td>
      </Table.Tr>
    );
  }

  const group = competition.data.groups.find((g) => g.id == game.groupId);
  const matchday = competition.data.matchdays.find(
    (m) => m.id == game.matchdayId,
  );
  const date = DateTime.fromISO(game.dateTime);

  return (
    <Table.Tr>
      <Table.Td>
        <Link to={"games/" + game.id}>{game.name}</Link>
      </Table.Td>
      <Table.Td>
        <Group>
          {date.toFormat("dd.MM., HH:mm")} Uhr{" "}
          {!game.started && (
            <ActionIcon
              component={Link}
              to={`games/${game.id}/change-datetime`}
            >
              <IconEdit size={18} />
            </ActionIcon>
          )}
        </Group>
      </Table.Td>
      <Table.Td>
        {game.actualTeams.homeTeamId ? (
          <TeamName teamId={game.actualTeams.homeTeamId} />
        ) : (
          "noch nicht bestätigt"
        )}
        <Text c="dimmed">
          <ResolvedTeamReference
            teamReference={game.plannedTeams.home}
            competitionId={competition.data.id}
          />
        </Text>
      </Table.Td>
      <Table.Td>
        {game.actualTeams.guestTeamId ? (
          <TeamName teamId={game.actualTeams.guestTeamId} />
        ) : (
          "noch nicht bestätigt"
        )}
        <Text c="dimmed">
          <ResolvedTeamReference
            teamReference={game.plannedTeams.guest}
            competitionId={competition.data.id}
          />
        </Text>
      </Table.Td>
      <Table.Td>{group ? group.name : ""}</Table.Td>
      <Table.Td>{matchday ? matchday.name : ""}</Table.Td>
      <Table.Td>
        <Group>
          <Time time={game.duration} />
          {!game.started && (
            <ActionIcon
              component={Link}
              to={`games/${game.id}/change-duration`}
            >
              <IconEdit size={18} />
            </ActionIcon>
          )}
        </Group>
      </Table.Td>
      <Table.Td>
        <Group>
          {game.shootOutShotsNumber}
          {!game.started && (
            <ActionIcon
              component={Link}
              to={`games/${game.id}/change-shoot-out-shots-number`}
            >
              <IconEdit size={18} />
            </ActionIcon>
          )}
        </Group>
      </Table.Td>
      <Table.Td>
        <Indicator
          disabled={!(game.started && !game.result)}
          color="red"
          processing
          position="middle-end"
        >
          {game.started && (
            <>
              {game.score.home}:{game.score.guest}
            </>
          )}
          {game.shootOutStarted && (
            <>
              <> </>(
              {
                game.shootOutShots.filter(
                  (s) => s.teamId === game.actualTeams.homeTeamId && s.success,
                ).length
              }
              :
              {
                game.shootOutShots.filter(
                  (s) => s.teamId === game.actualTeams.guestTeamId && s.success,
                ).length
              }
              )
            </>
          )}
          {!game.started && <Text c="dimmed">-</Text>}
        </Indicator>
      </Table.Td>
    </Table.Tr>
  );
};

export const CompetitionDetail: FC = () => {
  const { competitionId } = useRequiredParams(["competitionId"]);
  const competitionData = useCompetition(competitionId);

  const games = useGameList(competitionId);

  if (!games.isSuccess || !competitionData.isSuccess) {
    return <Loader />;
  }

  return (
    <Page
      title={competitionData.data.name}
      action={
        <Group gap="xs" wrap="wrap">
          <Button component={Link} to="games/new">
            Neues Spiel
          </Button>
          <Button component={Link} to="groups">
            Organisation
          </Button>
          <Button
            component="a"
            href={`/api/v1/competition.read/playerlist.pdf?competitionId=${competitionId}`}
            target="_blank"
            variant="light"
            leftSection={<IconListNumbers size={16} />}
          >
            Spielerliste
          </Button>
          <Button
            component="a"
            href={`/api/v1/competition.read/teamroster.zip?competitionId=${competitionId}`}
            target="_blank"
            variant="light"
            leftSection={<IconDownload size={16} />}
          >
            Team-Roster
          </Button>
        </Group>
      }
    >
      <Table.ScrollContainer minWidth={900}>
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Name</Table.Th>
              <Table.Th>Zeit</Table.Th>
              <Table.Th>Heim</Table.Th>
              <Table.Th>Gast</Table.Th>
              <Table.Th>Gruppe</Table.Th>
              <Table.Th>Matchday</Table.Th>
              <Table.Th>Dauer</Table.Th>
              <Table.Th>9 m</Table.Th>
              <Table.Th>Ergebnis</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {games.data.games.map((g) => (
              <TableRow key={g.id} game={g} />
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
    </Page>
  );
};
